/**
 * BrightPath - Expense Store (Zustand)
 * BP-04-04: Expenses Frontend
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { ExpenseCategory } from '../types';

type ViewMode = 'list' | 'grid' | 'table';
type SortField = 'expenseDate' | 'amount' | 'category' | 'createdAt';
type SortOrder = 'asc' | 'desc';

interface ExpenseUIState {
  // View preferences
  viewMode: ViewMode;
  setViewMode: (mode: ViewMode) => void;

  // Sorting
  sortField: SortField;
  sortOrder: SortOrder;
  setSort: (field: SortField, order?: SortOrder) => void;

  // Pagination
  pageSize: number;
  setPageSize: (size: number) => void;

  // Selected expenses (for bulk operations)
  selectedExpenseIds: string[];
  selectExpense: (id: string) => void;
  deselectExpense: (id: string) => void;
  toggleExpenseSelection: (id: string) => void;
  selectAllExpenses: (ids: string[]) => void;
  clearSelection: () => void;

  // Dashboard period
  dashboardYear: number;
  dashboardMonth: number | null; // null = YTD
  setDashboardPeriod: (year: number, month: number | null) => void;

  // Budget period
  budgetYear: number;
  budgetMonth: number;
  setBudgetPeriod: (year: number, month: number) => void;

  // Modals
  isImportModalOpen: boolean;
  setImportModalOpen: (open: boolean) => void;
  isExportModalOpen: boolean;
  setExportModalOpen: (open: boolean) => void;
  isQuickAddOpen: boolean;
  setQuickAddOpen: (open: boolean) => void;

  // Quick filters (frequently used)
  quickFilters: {
    taxDeductibleOnly: boolean;
    insuranceOnly: boolean;
    recentOnly: boolean; // Last 30 days
  };
  toggleQuickFilter: (filter: keyof ExpenseUIState['quickFilters']) => void;
  resetQuickFilters: () => void;

  // Chart preferences
  chartType: 'pie' | 'donut' | 'bar';
  setChartType: (type: 'pie' | 'donut' | 'bar') => void;
  showTrendChart: boolean;
  setShowTrendChart: (show: boolean) => void;

  // Favorite categories (for quick add)
  favoriteCategories: ExpenseCategory[];
  addFavoriteCategory: (category: ExpenseCategory) => void;
  removeFavoriteCategory: (category: ExpenseCategory) => void;
}

const now = new Date();

export const useExpenseStore = create<ExpenseUIState>()(
  persist(
    (set) => ({
      // View preferences
      viewMode: 'table',
      setViewMode: (mode) => set({ viewMode: mode }),

      // Sorting
      sortField: 'expenseDate',
      sortOrder: 'desc',
      setSort: (field, order) =>
        set((state) => ({
          sortField: field,
          sortOrder: order ?? (state.sortField === field && state.sortOrder === 'desc' ? 'asc' : 'desc'),
        })),

      // Pagination
      pageSize: 20,
      setPageSize: (size) => set({ pageSize: size }),

      // Selection
      selectedExpenseIds: [],
      selectExpense: (id) =>
        set((state) => ({
          selectedExpenseIds: [...state.selectedExpenseIds, id],
        })),
      deselectExpense: (id) =>
        set((state) => ({
          selectedExpenseIds: state.selectedExpenseIds.filter((i) => i !== id),
        })),
      toggleExpenseSelection: (id) =>
        set((state) => ({
          selectedExpenseIds: state.selectedExpenseIds.includes(id)
            ? state.selectedExpenseIds.filter((i) => i !== id)
            : [...state.selectedExpenseIds, id],
        })),
      selectAllExpenses: (ids) => set({ selectedExpenseIds: ids }),
      clearSelection: () => set({ selectedExpenseIds: [] }),

      // Dashboard period
      dashboardYear: now.getFullYear(),
      dashboardMonth: now.getMonth() + 1,
      setDashboardPeriod: (year, month) =>
        set({ dashboardYear: year, dashboardMonth: month }),

      // Budget period
      budgetYear: now.getFullYear(),
      budgetMonth: now.getMonth() + 1,
      setBudgetPeriod: (year, month) =>
        set({ budgetYear: year, budgetMonth: month }),

      // Modals
      isImportModalOpen: false,
      setImportModalOpen: (open) => set({ isImportModalOpen: open }),
      isExportModalOpen: false,
      setExportModalOpen: (open) => set({ isExportModalOpen: open }),
      isQuickAddOpen: false,
      setQuickAddOpen: (open) => set({ isQuickAddOpen: open }),

      // Quick filters
      quickFilters: {
        taxDeductibleOnly: false,
        insuranceOnly: false,
        recentOnly: false,
      },
      toggleQuickFilter: (filter) =>
        set((state) => ({
          quickFilters: {
            ...state.quickFilters,
            [filter]: !state.quickFilters[filter],
          },
        })),
      resetQuickFilters: () =>
        set({
          quickFilters: {
            taxDeductibleOnly: false,
            insuranceOnly: false,
            recentOnly: false,
          },
        }),

      // Chart preferences
      chartType: 'donut',
      setChartType: (type) => set({ chartType: type }),
      showTrendChart: true,
      setShowTrendChart: (show) => set({ showTrendChart: show }),

      // Favorite categories
      favoriteCategories: ['medical', 'therapy', 'medication'],
      addFavoriteCategory: (category) =>
        set((state) => ({
          favoriteCategories: state.favoriteCategories.includes(category)
            ? state.favoriteCategories
            : [...state.favoriteCategories, category],
        })),
      removeFavoriteCategory: (category) =>
        set((state) => ({
          favoriteCategories: state.favoriteCategories.filter((c) => c !== category),
        })),
    }),
    {
      name: 'brightpath-expense-store',
      partialize: (state) => ({
        viewMode: state.viewMode,
        pageSize: state.pageSize,
        sortField: state.sortField,
        sortOrder: state.sortOrder,
        chartType: state.chartType,
        showTrendChart: state.showTrendChart,
        favoriteCategories: state.favoriteCategories,
      }),
    }
  )
);
